import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, MapPin, Globe2, Building2, Clock } from "lucide-react";
import api from "@/lib/api";
import { Tier, Empty } from "@/components/Shared";
import { buildJobPostingLd } from "@/lib/jobPostingLd";
import ShareMenu from "@/components/ShareMenu";
import { jobShareText, jobShareUrl, jobSalaryText } from "@/lib/jobShare";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [notfound, setNotfound] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setNotfound(true));
  }, [id]);

  if (notfound) return <Empty text="Job not found." />;
  if (!job) return <Empty text="Loading…" />;

  // Normalize fields across live-feed jobs and paid jobs.
  const company = job.company_name || job.company;
  const jobType = job.employment_type || job.job_type;
  const isRemote = job.remote ?? job.is_remote;
  const salary = jobSalaryText(job);
  const meta = [
    { k: "company", icon: Building2, v: company, link: job.company_id && job.company_id !== "seed" ? `/company/${job.company_id}` : null },
    { k: "location", icon: MapPin, v: job.location },
    { k: "country", icon: Globe2, v: job.country },
    { k: "type", icon: Clock, v: jobType },
  ].filter((m) => m.v);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="job-detail-page">
      <Seo
        title={`${job.title}${company ? ` at ${company}` : ""} | WorldAIInfo`}
        description={`${job.title}${company ? ` at ${company}` : ""}${job.location ? ` — ${job.location}` : ""}. Apply for this AI role on WorldAIInfo.`.slice(0, 300)}
        canonicalPath={`/jobs/${id}`}
        type="article"
        jsonLd={buildJobPostingLd(job)}
      />
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00F0FF] mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </Link>
      <div className={`rounded-2xl p-8 ${job.tier === "featured" ? "card-surface featured-glow" : "card-surface"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="font-display font-extrabold text-3xl">{job.title}</h1>
            <Tier tier={job.tier} />
            {isRemote && <span className="font-mono-tech text-xs text-[#10B981]">REMOTE</span>}
          </div>
          <ShareMenu url={jobShareUrl(job)} text={jobShareText(job)} testId="job-detail-share" size="lg" trackId={job.id} />
        </div>
        {salary && <p className="font-display font-bold text-xl text-[#00F0FF]">{salary}</p>}
        <div className="flex flex-wrap gap-3 mt-5">
          {meta.map((m) => (
            m.link ? (
              <Link key={m.k} to={m.link} data-testid="job-detail-company"
                className="font-mono-tech text-xs flex items-center gap-1.5 border border-white/10 rounded px-3 py-1.5 text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition">
                <m.icon className="w-3.5 h-3.5" /> {m.v}
              </Link>
            ) : (
              <span key={m.k} className="font-mono-tech text-xs flex items-center gap-1.5 border border-white/10 rounded px-3 py-1.5 text-[#94A3B8]">
                <m.icon className="w-3.5 h-3.5" /> {m.v}
              </span>
            )
          ))}
        </div>
        <p className="text-[#94A3B8] mt-6 leading-relaxed whitespace-pre-line">{job.description}</p>
        <a href={job.apply_url} target="_blank" rel="noreferrer" data-testid="job-detail-apply"
          className="inline-flex items-center gap-2 mt-8 bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition">
          Apply now <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
